import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UserService } from '@services/user/user.service';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TabsComponent } from '@components/tabs/tabs.component';
import { combineLatest, filter, Observable, Subject } from 'rxjs';
import { Tab } from '@components/tabs/tab.interface';
import { distinctUntilChanged, map, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';
import { User } from '@app/shared/models/user.interface';
import { managingCompanyAdministrationTabs } from '@app/managing-company-administration/layout/managing-company-administration-tabs';
import { HeaderService } from '@services/header.service';
import { IsFeatureAvailableDirective } from '@app/shared/directives/is-feature-available.directive';
import { AsyncPipe } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { ManagingCompany } from '@app/shared/models/building.interface';
import { ManagingCompanyAuthorityType } from '@app/shared/models/managing-company-authorities.interface';
import { ManagingCompanyService } from '@services/managing-company.service';
import { ToastService } from '@services/toast/toast.service';
import { IsAuthorizedForManagingCompanyDirective } from '@app/shared/directives/is-authorized-for-managing-company.directive';

@Component({
  selector: 'app-managing-company-administration',
  imports: [
    RouterOutlet,
    TabsComponent,
    IsFeatureAvailableDirective,
    MatFormField,
    MatSelect,
    MatOption,
    AsyncPipe,
    IsAuthorizedForManagingCompanyDirective,
    MatLabel
  ],
  templateUrl: './managing-company-administration.component.html',
  styleUrl: './managing-company-administration.component.scss'
})
export class ManagingCompanyAdministrationComponent implements OnInit, OnDestroy {
  public tabs$: Observable<Tab[]>;
  public adminManagingCompanies$: Observable<ManagingCompany[]>;
  public selectedManagingCompanyId: number | null = null;
  private pageUrl = '/managing-company-administration';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly header: HeaderService,
    private readonly userService: UserService,
    private readonly managingCompanyService: ManagingCompanyService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly toastService: ToastService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.header.showUserMenu();
    this.header.showSessionMenu();

    const currentUser$ = this.userService.getCurrentUser().pipe(shareReplay(1));
    const managingCompanies$ = this.managingCompanyService.getManagingCompanies().pipe(shareReplay(1));

    const managingCompanyIdFromUrl$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => {
        const parsed = Number(this.activatedRoute.firstChild?.snapshot.paramMap.get('managingCompanyId'));
        return Number.isFinite(parsed) ? parsed : null;
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.tabs$ = combineLatest([currentUser$, managingCompanyIdFromUrl$]).pipe(
      map(([user, mcId]) => this.createManagingCompanyAdministrationMenu(user, mcId)),
      shareReplay(1)
    );

    this.adminManagingCompanies$ = combineLatest([currentUser$, managingCompanies$]).pipe(
      map(([user, companies]) => this.filterAdminManagingCompanies(user, companies)),
      shareReplay(1)
    );

    this.handleNoManagingCompanyAccess();
    this.syncSelectedManagingCompanyWithUrl(managingCompanyIdFromUrl$);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onManagingCompanyChanged(managingCompanyId: number): void {
    this.selectedManagingCompanyId = managingCompanyId;
    this.changeDetectorRef.markForCheck();

    this.userService.getCurrentUser().pipe(take(1))
      .subscribe((user) => {
        const availableTabs = this.createManagingCompanyAdministrationMenu(user, managingCompanyId);
        const currentTab = this.router.url.split('/').pop();
        const matchingTab = availableTabs.find((tab) => tab.path.endsWith(`/${currentTab}`));
        const targetPath = matchingTab?.path ?? availableTabs[0]?.path;

        if (targetPath) {
          this.router.navigateByUrl(targetPath);
        }
      });
  }

  private handleNoManagingCompanyAccess(): void {
    this.adminManagingCompanies$
      .pipe(
        filter((mcs) => mcs.length === 0),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.selectedManagingCompanyId = null;
        this.changeDetectorRef.markForCheck();

        this.toastService.error({
          message: 'You do not have access to any managing company.',
          dataCy: 'no-managing-company-access'
        });

        this.router.navigate([this.pageUrl], { replaceUrl: true });
      });
  }

  private syncSelectedManagingCompanyWithUrl(managingCompanyIdFromUrl$: Observable<number | null>): void {
    combineLatest([this.adminManagingCompanies$, managingCompanyIdFromUrl$, this.userService.getCurrentUser()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([adminCompanies, idFromUrl, user]) => {
        if (!adminCompanies.length) return;

        const initialId = this.pickInitialManagingCompanyId(adminCompanies, idFromUrl);
        this.selectedManagingCompanyId = initialId;
        this.changeDetectorRef.markForCheck();

        const availableTabs = this.createManagingCompanyAdministrationMenu(user, initialId);
        const currentUrlBelongsToSelectedManagingCompany = this.router.url.includes(`${this.pageUrl}/${initialId}/`);
        const currentUrlMatchesAuthorizedTab = availableTabs.some((tab) => tab.path === this.router.url);

        if (currentUrlBelongsToSelectedManagingCompany && currentUrlMatchesAuthorizedTab) {
          return;
        }

        const defaultTabPath = availableTabs[0]?.path;
        if (defaultTabPath) {
          this.router.navigateByUrl(defaultTabPath, { replaceUrl: true });
        }
      });
  }

  private pickInitialManagingCompanyId(adminCompanies: ManagingCompany[], idFromUrl: number | null): number {
    if (idFromUrl != null && adminCompanies.some((mc) => mc.id === idFromUrl)) {
      return idFromUrl;
    }
    return adminCompanies[0].id;
  }

  private filterAdminManagingCompanies(user: User, companies: ManagingCompany[]): ManagingCompany[] {
    const allowedIds = new Set(
      (user.managingCompanyAuthorities ?? [])
        .filter(
          (mca) =>
            (mca.authorities ?? []).includes(ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN) ||
            (mca.authorities ?? []).includes(ManagingCompanyAuthorityType.MANAGE_GLOBAL_AUTHORITY_REQUEST)
        )
        .map((mca) => mca.managingCompanyId)
    );

    return (companies ?? []).filter((mc) => allowedIds.has(mc.id));
  }

  private createManagingCompanyAdministrationMenu(user: User, managingCompanyId: number | null): Tab[] {
    if (managingCompanyId == null) {
      return [];
    }

    return managingCompanyAdministrationTabs
      .filter((tab) => this.hasSelectedManagingCompanyAuthority(user, managingCompanyId, tab.requiredAuthority))
      .map((tab) => ({ ...tab, path: tab.path.replace(/\{managingCompanyId}/i, String(managingCompanyId)) }));
  }

  private hasSelectedManagingCompanyAuthority(user: User, managingCompanyId: number, requiredAuthority: ManagingCompanyAuthorityType): boolean {
    const managingCompanyAuthorities =
      user.managingCompanyAuthorities?.find((mca) => mca.managingCompanyId === managingCompanyId)?.authorities ?? [];
    return managingCompanyAuthorities.includes(requiredAuthority);
  }

  protected readonly ManagingCompanyAuthorityType = ManagingCompanyAuthorityType;
}
